import { useMutation } from '@tanstack/react-query';

import queryClient from '../../../lib/queryClient';
import { triggerSnackbar } from '../../../provider/SnackbarProvider';

import { createMedication } from '../../../apis/medication';
import { handleErrorResponse } from '../../../utils/handleError';

import {
  CreateMedicationPayload,
  TDosageFrequency,
  UseCreateMedicationResult,
} from './types';

export function useCreateMedication(): UseCreateMedicationResult {
  const { mutate, isSuccess } = useMutation({
    mutationKey: ['create-medication'],
    mutationFn: (payload: CreateMedicationPayload) => createMedication(payload),
    onSuccess: (data) => {
      const message = data?.message;
      if (message) {
        triggerSnackbar({
          message,
          severity: 'success',
        });
      }
      queryClient.invalidateQueries({ queryKey: ['medication-list'] });
      queryClient.invalidateQueries({ queryKey: ['medication-reminder-list'] });
      queryClient.invalidateQueries({ queryKey: ['medication-consistency'] });
    },
    onError: (error) => handleErrorResponse(error, 'Medication Creation'),
  });

  const submitPatientMedication = (params: {
    name: string;
    dosage: string;
    frequency: string;
    details?: string;
    patientId?: number;
    reminder: boolean;
    dosageFrequency?: TDosageFrequency | null;
    reminderStartDate: Date;
    reminderEndDate: Date | null;
  }) => {
    const payload: CreateMedicationPayload = {
      medication: {
        name: params.name,
        dosage: params.dosage,
        frequency: params.frequency,
        instruction: params.details,
        patient_id: params.patientId,
        reminder: params.reminder,
        dosage_frequency: params.dosageFrequency,
        reminder_start_date: params.reminderStartDate,
        reminder_end_date: params.reminderEndDate,
      },
    };

    mutate(payload);
  };

  return {
    submitPatientMedication,
    isSuccess,
  };
}
